/**
 * Work-permit / immigration-reporting keyword matrix — deterministic
 * generator (no AI credits). Axes: topic × nationality × city × intent.
 * No prices, no guarantees.
 */
import { WP_TOPICS } from "./index";

export const WP_CITIES: readonly { slug: string; th: string }[] = [
  { slug: "bangkok", th: "กรุงเทพ" },
  { slug: "nonthaburi", th: "นนทบุรี" },
  { slug: "pathumthani", th: "ปทุมธานี" },
  { slug: "samutprakan", th: "สมุทรปราการ" },
  { slug: "chonburi", th: "ชลบุรี" },
  { slug: "pattaya", th: "พัทยา" },
  { slug: "rayong", th: "ระยอง" },
  { slug: "chiangmai", th: "เชียงใหม่" },
  { slug: "phuket", th: "ภูเก็ต" },
  { slug: "suratthani", th: "สุราษฎร์ธานี" },
  { slug: "khonkaen", th: "ขอนแก่น" },
  { slug: "udonthani", th: "อุดรธานี" },
  { slug: "nakhonratchasima", th: "นครราชสีมา" },
  { slug: "songkhla", th: "สงขลา" },
  { slug: "ayutthaya", th: "อยุธยา" },
] as const;

export const WP_NATIONALITIES: readonly { slug: string; th: string; en: string }[] = [
  { slug: "japanese", th: "ญี่ปุ่น", en: "Japanese" },
  { slug: "chinese", th: "จีน", en: "Chinese" },
  { slug: "korean", th: "เกาหลี", en: "Korean" },
  { slug: "indian", th: "อินเดีย", en: "Indian" },
  { slug: "american", th: "อเมริกัน", en: "American" },
  { slug: "british", th: "อังกฤษ", en: "British" },
  { slug: "australian", th: "ออสเตรเลีย", en: "Australian" },
  { slug: "german", th: "เยอรมัน", en: "German" },
  { slug: "french", th: "ฝรั่งเศส", en: "French" },
  { slug: "russian", th: "รัสเซีย", en: "Russian" },
  { slug: "vietnamese", th: "เวียดนาม", en: "Vietnamese" },
  { slug: "myanmar", th: "เมียนมา", en: "Burmese" },
  { slug: "filipino", th: "ฟิลิปปินส์", en: "Filipino" },
  { slug: "singaporean", th: "สิงคโปร์", en: "Singaporean" },
  { slug: "taiwanese", th: "ไต้หวัน", en: "Taiwanese" },
] as const;

export const WP_INTENTS: readonly { slug: string; th: string }[] = [
  { slug: "new-employee", th: "รับพนักงานต่างชาติใหม่" },
  { slug: "change-employer", th: "ย้ายนายจ้าง" },
  { slug: "renew", th: "ต่ออายุใบอนุญาต" },
  { slug: "extend-stay", th: "ขออยู่ต่อ 1 ปี" },
  { slug: "driving-licence", th: "ทำใบขับขี่" },
  { slug: "buy-vehicle", th: "ซื้อรถ/จักรยานยนต์" },
  { slug: "open-bank-account", th: "เปิดบัญชีธนาคาร" },
  { slug: "travel-abroad", th: "เดินทางออกนอกประเทศชั่วคราว" },
  { slug: "move-house", th: "ย้ายที่พัก" },
  { slug: "condo-landlord", th: "เจ้าของคอนโดให้เช่า" },
  { slug: "startup-company", th: "เปิดบริษัทใหม่" },
  { slug: "apec-business", th: "เดินทางธุรกิจในเอเปค" },
  { slug: "teacher", th: "ครู/อาจารย์ต่างชาติ" },
  { slug: "engineer", th: "วิศวกรต่างชาติ" },
] as const;

const PREFIXES = [
  "ขอ",
  "ยื่น",
  "บริการ",
  "ขั้นตอน",
  "เอกสาร",
  "ที่ปรึกษา",
  "ตัวแทนยื่น",
] as const;

function build(): string[] {
  const out = new Set<string>();

  for (const t of WP_TOPICS) {
    out.add(t.th);
    out.add(`${t.th} คืออะไร`);
    out.add(`${t.th} ต้องเตรียมเอกสารอะไร`);
    out.add(`${t.th} ใช้เวลากี่วันทำการ`);
    out.add(`${t.th} ยื่นที่ไหน`);
    out.add(`${t.th} ถูกตีกลับเพราะอะไร`);
    out.add(t.en.toLowerCase());
    out.add(`${t.en.toLowerCase()} thailand`);
    out.add(`${t.en.toLowerCase()} requirements`);
    for (const p of PREFIXES) out.add(`${p}${t.th}`);
    for (const c of WP_CITIES) {
      out.add(`${t.th} ${c.th}`);
      out.add(`${t.en.toLowerCase()} ${c.slug}`);
    }
    for (const n of WP_NATIONALITIES) {
      out.add(`${t.th} สำหรับคน${n.th}`);
      out.add(`${t.en.toLowerCase()} for ${n.en.toLowerCase()} nationals`);
    }
    for (const i of WP_INTENTS) out.add(`${t.th} กรณี${i.th}`);
  }

  for (const i of WP_INTENTS) {
    out.add(`${i.th} ต้องใช้เอกสารอะไร`);
    out.add(`${i.th} ต้องแจ้งตรวจคนเข้าเมืองไหม`);
    out.add(`${i.th} ขั้นตอนทำอย่างไร`);
    for (const c of WP_CITIES) out.add(`${i.th} ${c.th}`);
  }

  for (const c of WP_CITIES) {
    out.add(`ใบอนุญาตทำงาน ${c.th}`);
    out.add(`รายงานตัว 90 วัน ${c.th}`);
    out.add(`แจ้งที่พัก ตม.30 ${c.th}`);
    out.add(`re-entry permit ${c.slug}`);
    out.add(`work permit ${c.slug}`);
    out.add(`หนังสือรับรองถิ่นที่อยู่ ${c.th}`);
  }

  for (const n of WP_NATIONALITIES) {
    out.add(`ใบอนุญาตทำงานคน${n.th}`);
    out.add(`work permit thailand for ${n.en.toLowerCase()}`);
    out.add(`รายงานตัว 90 วัน คน${n.th}`);
  }

  // long-tail question-style keys
  const LONGTAIL = [
    "ใบอนุญาตทำงานหมดอายุแล้วต้องทำอย่างไร",
    "ยังไม่ได้ work permit ทำงานได้ไหม",
    "รายงานตัว 90 วัน เกินกำหนดปรับเท่าไหร่",
    "รายงานตัว 90 วัน ออนไลน์ทำอย่างไร",
    "ตม.30 ใครมีหน้าที่แจ้ง",
    "ตม.30 กับ ตม.47 ต่างกันอย่างไร",
    "re-entry permit single กับ multiple เลือกแบบไหน",
    "ลืมทำ re-entry permit ก่อนบินออกทำอย่างไร",
    "หนังสือรับรองถิ่นที่อยู่ขอที่ไหน",
    "หนังสือรับรองถิ่นที่อยู่ใช้ทำใบขับขี่ได้ไหม",
    "เปลี่ยนนายจ้างต้องแก้ใบอนุญาตทำงานไหม",
    "abtc สมัครที่ไหน",
    "abtc ใช้ทำงานในต่างประเทศได้ไหม",
    "ใบอนุญาตทำงานกับวีซ่าต่างกันอย่างไร",
    "เปลี่ยนเล่มพาสปอร์ตต้องแจ้งตรวจคนเข้าเมืองไหม",
  ];
  for (const k of LONGTAIL) out.add(k);

  return Array.from(out);
}

export const WP_KEYWORDS: string[] = build();
export const WP_KEYWORD_COUNT = WP_KEYWORDS.length;
